import React, { useEffect, useRef, useState } from 'react';
import { getSettings } from '../services/api';
import { Instagram, Play } from 'lucide-react';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80';

const isVideoUrl = (u) => /\.(mp4|webm|mov)(\?|$)/i.test(u || '');

// Normalize tile: strings (legacy) OR objects {url, link, kind}
const normalize = (item) => {
  if (!item) return null;
  if (typeof item === 'string') {
    return item ? { url: item, link: '', kind: isVideoUrl(item) ? 'video' : 'image' } : null;
  }
  if (!item.url) return null;
  return {
    url: item.url,
    link: item.link || '',
    kind: item.kind || (isVideoUrl(item.url) ? 'video' : 'image'),
  };
};

/**
 * Lazy-loaded video tile — only requests bytes when close to viewport.
 * Uses IntersectionObserver + `preload="none"`. Autoplay muted+loop when in view.
 */
const VideoTile = ({ src }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="absolute inset-0">
      {inView ? (
        <video
          src={src}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <Play size={28} className="text-gray-400" strokeWidth={1.5} />
        </div>
      )}
    </div>
  );
};

const InstagramFeed = () => {
  const [settings, setSettings] = useState({ instagram: '', instagramPosts: [] });

  useEffect(() => {
    getSettings()
      .then((r) => setSettings((s) => ({ ...s, ...(r.data || {}) })))
      .catch(() => {});
  }, []);

  const profileUrl = settings.instagram || 'https://instagram.com/camption';
  const tiles = (settings.instagramPosts || []).map(normalize).filter(Boolean).slice(0, 6);
  const fillCount = Math.max(0, 6 - tiles.length);
  const placeholderTiles = Array(fillCount).fill(null).map(() => ({ url: PLACEHOLDER, link: '', kind: 'image' }));
  const finalTiles = [...tiles, ...placeholderTiles];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="instagram-feed">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-2">
              Instagram
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#1B1B1B] leading-tight"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Anlarımızı takip edin
            </h2>
          </div>
          <a
            href={profileUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow-button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B1B1B] hover:text-[#3D4A26] transition-colors w-fit"
          >
            <Instagram size={16} />
            @camption
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
          {finalTiles.map((tile, i) => {
            const href = tile.link || profileUrl;
            const isVideo = tile.kind === 'video';
            return (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`instagram-tile-${i}`}
                className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100"
              >
                {isVideo ? (
                  <VideoTile src={tile.url} />
                ) : (
                  <img
                    src={tile.url}
                    alt={`Camption Instagram gönderisi ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                )}
                {isVideo && (
                  <span className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                    Reel
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-[#833AB4]/70 via-[#FD1D1D]/70 to-[#FCB045]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Instagram size={28} className="text-white" strokeWidth={1.5} />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
